/** Hard cap for the multi-hop executor tool loop. */
export const MAX_EXECUTOR_ITERATIONS = 5_000;

/** Worksheet rows resolved by one ID-lookup wave. */
export const ID_LOOKUP_WORKLOAD_WAVE_SIZE = 20;

/** One model iteration starts the workload before its alternating waves. */
export const ID_LOOKUP_WORKLOAD_START_ITERATIONS = 1;

/** Each wave needs one lookup iteration and one commit/verification iteration. */
export const ID_LOOKUP_WORKLOAD_ITERATIONS_PER_WAVE = 2;

/**
 * Reserved iterations for plan/status recovery and the final response. Keeping
 * this headroom prevents the largest accepted workload from consuming the
 * executor's entire loop with start/next/commit calls.
 */
export const ID_LOOKUP_WORKLOAD_TERMINAL_ITERATION_HEADROOM = 19;

/** Maximum company labels sent to one tabular column execution call. */
export const TABULAR_COLUMN_BATCH_SIZE = 15;

/** Maximum independent cell targets sent through one BFF fanout request. */
export const TABULAR_CELL_TRANSPORT_BATCH_SIZE = 20;

/** Independent FactSet identity lookups used by a tabular active wave. */
export const TABULAR_IDENTITY_LOOKUP_CONCURRENCY = 4;

/** Cell-mode workloads above this executable-row count fail preflight. */
export const TABULAR_CELL_MAX_EXECUTABLE_ROWS = 1_100;

/** The HTTP metadata facade currently accepts at most this many targets. */
export const TABULAR_METADATA_MAX_TARGETS = 2_000;

/**
 * Selected user files copied onto every tabular target. Mirrors
 * `MAX_FILES_PER_TARGET` in the data-feed tabular execution models, which
 * rejects a larger selection with a 422.
 */
export const TABULAR_MAX_USER_FILE_IDS_PER_TARGET = 100;

/** One model iteration registers and preflights a tabular workload. */
export const TABULAR_WORKLOAD_START_ITERATIONS = 1;

/** Every accepted tabular wave requires one execute and one commit iteration. */
export const TABULAR_WORKLOAD_ITERATIONS_PER_WAVE = 2;

/** Plan/status recovery and final-response headroom for tabular workloads. */
export const TABULAR_WORKLOAD_TERMINAL_ITERATION_HEADROOM = 19;

const ID_LOOKUP_MAX_WAVES = Math.floor(
  (MAX_EXECUTOR_ITERATIONS -
    ID_LOOKUP_WORKLOAD_START_ITERATIONS -
    ID_LOOKUP_WORKLOAD_TERMINAL_ITERATION_HEADROOM) /
    ID_LOOKUP_WORKLOAD_ITERATIONS_PER_WAVE,
);

/** Maximum nonblank worksheet rows accepted by one lookup workload. */
export const ID_LOOKUP_MAX_NONBLANK_ROWS =
  ID_LOOKUP_MAX_WAVES * ID_LOOKUP_WORKLOAD_WAVE_SIZE;

export interface ExecutorWorkloadIterationBudget {
  rowsPerWave: number;
  startIterations: number;
  iterationsPerWave: number;
  terminalIterationHeadroom: number;
}

/** Iterations required by a finite start/wave/commit worksheet workload. */
export function requiredExecutorIterationsForWorkload(
  rowCount: number,
  budget: ExecutorWorkloadIterationBudget,
): number {
  const normalizedRowCount = Math.max(0, Math.floor(rowCount));
  const normalizedRowsPerWave = Math.max(1, Math.floor(budget.rowsPerWave));
  const waveCount = Math.ceil(normalizedRowCount / normalizedRowsPerWave);
  return (
    budget.startIterations +
    waveCount * budget.iterationsPerWave +
    budget.terminalIterationHeadroom
  );
}

/** Iterations required from workload registration through terminal headroom. */
export function requiredExecutorIterationsForIdLookup(rowCount: number): number {
  return requiredExecutorIterationsForWorkload(rowCount, {
    rowsPerWave: ID_LOOKUP_WORKLOAD_WAVE_SIZE,
    startIterations: ID_LOOKUP_WORKLOAD_START_ITERATIONS,
    iterationsPerWave: ID_LOOKUP_WORKLOAD_ITERATIONS_PER_WAVE,
    terminalIterationHeadroom:
      ID_LOOKUP_WORKLOAD_TERMINAL_ITERATION_HEADROOM,
  });
}

/** Clamp a server-selected workload concurrency once at run construction. */
export function clampTabularConcurrency(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value)
    ? Math.min(256, Math.max(1, Math.floor(value)))
    : 1;
}

/** Rows admitted to one tabular active wave, distinct from worker concurrency. */
export function tabularRowsPerWave(
  mode: "column" | "cell",
  limits: {
    columnMaxInFlightBatches: number;
    cellMaxInFlightSingles: number;
  },
): number {
  const batchConcurrency = clampTabularConcurrency(
    limits.columnMaxInFlightBatches,
  );
  if (mode === "column") {
    return TABULAR_COLUMN_BATCH_SIZE * batchConcurrency;
  }
  const cellBatchSize = Math.max(
    TABULAR_CELL_TRANSPORT_BATCH_SIZE,
    clampTabularConcurrency(limits.cellMaxInFlightSingles),
  );
  return Math.min(
    TABULAR_CELL_MAX_EXECUTABLE_ROWS,
    cellBatchSize * batchConcurrency,
  );
}

/** Full start/next/commit budget for one finite tabular workload. */
export function requiredExecutorIterationsForTabular(
  rowCount: number,
  mode: "column" | "cell",
  limits: {
    columnMaxInFlightBatches: number;
    cellMaxInFlightSingles: number;
  },
): number {
  return requiredExecutorIterationsForWorkload(rowCount, {
    rowsPerWave: tabularRowsPerWave(mode, limits),
    startIterations: TABULAR_WORKLOAD_START_ITERATIONS,
    iterationsPerWave: TABULAR_WORKLOAD_ITERATIONS_PER_WAVE,
    terminalIterationHeadroom:
      TABULAR_WORKLOAD_TERMINAL_ITERATION_HEADROOM,
  });
}

// The iteration cap above is a count and nothing more: a run can sit inside slow
// tools indefinitely, and it can burn every remaining iteration re-issuing one
// call that changes nothing. The two guards below close those gaps. Both read
// the run's own clock and progress rather than a provider stop reason, and both
// end the run through the existing partial-result path so applied edits survive.

/** Wall-clock budget for one executor run (5 hours). */
export const EXECUTOR_RUN_TIME_BUDGET_MS = 5 * 60 * 60 * 1000;

/** True once a run has spent its wall-clock budget. */
export function hasExhaustedExecutorRunTimeBudget(
  elapsedMs: number,
  budgetMs: number = EXECUTOR_RUN_TIME_BUDGET_MS,
): boolean {
  return elapsedMs >= budgetMs;
}

/**
 * Consecutive unproductive iterations tolerated before the run stops.
 *
 * Three is a retry plus a confirmation: a model that re-issues the same call
 * after seeing its own unchanged result twice more is not converging, and every
 * further iteration only spends tokens and latency.
 */
export const MAX_STAGNANT_EXECUTOR_ITERATIONS = 3;

/** Everything the stagnation guard counts as forward progress. */
export interface ExecutorProgressSignals {
  /** Plan progress: run steps the model has created. */
  planSteps: number;
  /** Status progress: the status currently assigned to each run step. */
  stepStatuses: readonly string[];
  /** Workbook progress: cells confirmed written to the host. */
  editedCells: number;
  /** Research progress: research calls that returned a usable answer. */
  researchAnswers: number;
  /** Finite worksheet workload progress, if this run owns one. */
  workloadProgress?: number;
  /**
   * Orchestrator progress: steering messages the runner itself has pushed.
   *
   * A repeated tool call is not a stalled loop while the runner is still
   * working through one of its own bounded reminder ladders - each rung gives
   * the model a new instruction and often a different forced tool, so the next
   * turn is not a repeat of this one. Every ladder has its own budget, so this
   * signal cannot keep the guard from firing indefinitely.
   */
  orchestratorMessages: number;
}

/** Collapses the progress signals into a comparable key. */
export function executorProgressSignature(
  signals: ExecutorProgressSignals,
): string {
  return [
    signals.planSteps,
    signals.stepStatuses.join(","),
    signals.editedCells,
    signals.researchAnswers,
    signals.workloadProgress ?? 0,
    signals.orchestratorMessages,
  ].join("|");
}

/** Key-order-independent JSON, so re-serialized arguments compare equal. */
function stableJson(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map(stableJson).join(",")}]`;
  }
  if (value && typeof value === "object") {
    const record = value as Record<string, unknown>;
    return `{${Object.keys(record)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${stableJson(record[key])}`)
      .join(",")}}`;
  }
  return JSON.stringify(value ?? null);
}

/**
 * Collapses one iteration's tool calls into a comparable key. Call ids are
 * excluded because they are new on every turn, and the calls are sorted so a
 * re-issued parallel batch compares equal whatever order it arrives in.
 */
export function normalizeExecutorToolCalls(
  calls: readonly { name: string; arguments: string }[],
): string {
  return calls
    .map((call) => {
      let args: unknown;
      try {
        args = JSON.parse(call.arguments);
      } catch {
        args = call.arguments.trim();
      }
      return `${call.name}(${stableJson(args)})`;
    })
    .sort()
    .join(" ");
}

export interface ExecutorStagnationBudget {
  /**
   * Records one tool-calling iteration. Returns true once the same normalized
   * tool calls have repeated for `maxStagnantIterations` consecutive iterations
   * without moving a single progress signal.
   */
  record(toolCalls: string, progress: string): boolean;
}

export function createExecutorStagnationBudget(
  maxStagnantIterations: number = MAX_STAGNANT_EXECUTOR_ITERATIONS,
): ExecutorStagnationBudget {
  let previousFingerprint: string | null = null;
  let stagnantIterations = 0;
  return {
    record(toolCalls, progress) {
      const fingerprint = `${toolCalls}#${progress}`;
      if (fingerprint === previousFingerprint) {
        stagnantIterations += 1;
      } else {
        previousFingerprint = fingerprint;
        stagnantIterations = 0;
      }
      return stagnantIterations >= maxStagnantIterations;
    },
  };
}
